'use client'
import { Logo } from '@/shared/logo'
import { Link } from '@/shared/i18n'
import { CategoryDto, GetLanguageMenuSettingsDto } from '@/shared/api/strapi/requests'
import { Button } from '@/shared/ui/button'
import {
  InstagramWhiteIcon,
  LinkedinWhiteIcon,
  LocationIcon,
  TelegramWhiteIcon,
  TtWhiteIcon,
  WhatsappHeaderWhiteIcon,
  WhatsappWhiteIcon,
} from '@/shared/icons'
import { parsePhoneNumber } from '@/shared/lib/utils'
import { ModalBlockDto } from '@/shared/api/strapi/requests/getModalsBlock'
import { useState } from 'react'
import { ChevronDownIcon } from 'lucide-react'
import * as React from 'react'
import { ContactModal } from '@/widgets/Contact/ContactModal'

export interface FooterProps {
  locale: string
  settings: GetLanguageMenuSettingsDto
  modalsBlock: ModalBlockDto
}

const Category = ({ category, parentPath }: { category: CategoryDto; parentPath: string }) => {
  const { children, name, slug } = category
  const [expanded, setExpanded] = useState(false)

  const path = `${parentPath}/${slug}`
  return (
    <div className={'flex flex-col content-center justify-start gap-1 font-medium'}>
      <div className={'flex flex-row items-center justify-start'}>
        <Link href={path}>{name}</Link>
        {children && children.length > 0 && (
          <Button
            variant={'ghost'}
            className={
              '[&[data-state=open]>svg]:rotate-180 bg-transparent hover:bg-transparent text-primary-foreground hover:text-primary-foreground h-full'
            }
            data-state={expanded ? 'open' : 'close'}
            onClick={() => setExpanded(!expanded)}
            size={'icon'}
          >
            <ChevronDownIcon className="pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200" />
            <span className="sr-only">Expand category</span>
          </Button>
        )}
      </div>
      {expanded && (
        <ul className={'list-disc pl-6'}>
          {children.map(item => (
            <li key={item.id}>
              <Category category={item} parentPath={parentPath} />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export const Footer = ({ locale, settings: { footer, footerMenu, dataSettings }, modalsBlock }: FooterProps) => {
  return (
    <footer className={'w-full bg-grey font-body font-light text-xs md:text-sm lg:text-base'}>
      <Button
        asChild
        size={'icon'}
        className="fixed bottom-4 right-4 cursor-pointer hover:bg-green-500 bg-green-600 rounded-full size-14 p-3"
      >
        <Link href={dataSettings.whatsapp}>
          <WhatsappHeaderWhiteIcon className={'size-8'} />
          <span className="sr-only">Whatsapp link</span>
        </Link>
      </Button>
      <div
        className={
          'gap-4 grid grid-cols-1 md:grid-cols-[auto_auto_auto] lg:grid-cols-[auto_auto_auto_auto] text-white pt-[15px] px-[30px] pb-[30px] md:p-[30px] lg:px-[80px] lg:py-[40px] overflow-hidden'
        }
      >
        <div className={'flex flex-row gap-2 justify-between'}>
          <Link href={{ pathname: '/' }} className={'justify-self-center'}>
            <Logo className={'h-14 fill-current'} />
            <span className="sr-only">Home link in footer</span>
          </Link>
          <div className={'flex md:hidden flex-col items-end justify-center gap-0.5 underline'}>
            <Link href={`tel:${parsePhoneNumber(dataSettings.phone)}`}>{parsePhoneNumber(dataSettings.phone)}</Link>
            <Link href={`mailto:${dataSettings.email}`}>{dataSettings.email}</Link>
          </div>
        </div>
        <nav className={'h-fit grid grid-cols-1 gap-2 font-medium place-items-start lg:place-items-start'}>
          {footer.map(item => (
            <Link locale={locale} key={item.id} href={item.Href} target={item?.Target_blank ? '_blank' : '_self'}>
              {item.Title}
            </Link>
          ))}
        </nav>
        <div className={'w-full flex flex-row justify-between md:justify-around items-start gap-6'}>
          {footerMenu.map(category => (
            <Category key={category.id} category={category} parentPath={'/services'} />
          ))}
        </div>
        <div className={'contents lg:flex flex-col content-center gap-6'}>
          <ContactModal dataSettings={dataSettings} modalsBlock={modalsBlock}>
            <Button
              className={
                'self-end row-start-2 w-full h-[52px] rounded-xl md:place-self-end md:max-w-[240px] md:col-start-3'
              }
              variant={'outline'}
            >
              {dataSettings.contact_me_button_text}
            </Button>
          </ContactModal>
          <div
            className={
              'flex flex-row content-center justify-between lg:justify-end gap-2 h-10 md:row-start-2 md:col-span-2'
            }
          >
            <Link href={dataSettings.whatsapp} className={'h-full'}>
              <WhatsappWhiteIcon className={'h-full'} />
              <span className="sr-only">Whatsapp link in footer</span>
            </Link>
            <Link href={dataSettings.instagram} className={'h-full'}>
              <InstagramWhiteIcon className={'h-full'} />
              <span className="sr-only">Instagram link in footer</span>
            </Link>
            <Link href={dataSettings.linkedin} className={'h-full'}>
              <LinkedinWhiteIcon className={'h-full'} />
              <span className="sr-only">Linkedin link in footer</span>
            </Link>
            <Link href={dataSettings.telegram} className={'h-full'}>
              <TelegramWhiteIcon className={'h-full'} />
              <span className="sr-only">Telegram link in footer</span>
            </Link>
            <Link href={dataSettings.youtube} className={'h-full'}>
              <TtWhiteIcon className={'h-full'} />
              <span className="sr-only">Youtube link in footer</span>
            </Link>
          </div>
          <div className={'hidden md:flex flex-col items-end justify-center gap-0.5 underline md:col-start-3'}>
            <Link href={`tel:${parsePhoneNumber(dataSettings.phone)}`}>{parsePhoneNumber(dataSettings.phone)}</Link>
            <Link href={`mailto:${dataSettings.email}`}>{dataSettings.email}</Link>
          </div>
          <Button
            variant={'secondary'}
            className={'lg:max-w-[240px] self-end w-full h-[52px] rounded-xl md:row-start-3 md:col-span-2'}
            asChild
          >
            <Link href={dataSettings.map} target={'_blank'}>
              {dataSettings.intearctive_map_button_text}
              <LocationIcon className={'fill-current'} />
            </Link>
          </Button>
        </div>
      </div>
      <div className={'flex flex-col items-start justify-center gap-0.5 underline text-white mx-auto px-10 pb-4'}>
        <Link className={'underline'} href={'/about/legal-documents'}>
          {dataSettings.personal_data_link_text}
        </Link>
      </div>
    </footer>
  )
}
